import React, { useState } from 'react';
import api from '../../services/api';
import authService from '../../services/auth';
import '../../styles/DocumentUploadModal.css';

const DocumentUploadModal = ({ show, onHide, entityType, entityId, onSuccess, required = false }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState({});

  const documentTypes = [
    'ID Proof',
    'Registration Form',
    'Payment Receipt',
    'Academic Transcript',
    'Passport Copy',
    'Visa Documents',
    'Medical Certificate',
    'Other'
  ];

  const addFile = () => {
    setFiles([...files, { file: null, documentType: '', id: Date.now() }]);
  };

  const removeFile = (id) => {
    setFiles(files.filter(f => f.id !== id));
  };

  const updateFile = (id, field, value) => {
    setFiles(files.map(f => f.id === id ? { ...f, [field]: value } : f));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validFiles = files.filter(f => f.file && f.documentType);
    if (validFiles.length === 0) {
      setError('Please add at least one document with type and file');
      return;
    }

    setLoading(true);
    setError('');
    setUploadProgress({});

    try {
      const uploadPromises = validFiles.map(async (fileData, index) => {
        const formData = new FormData();
        formData.append('document', fileData.file);
        formData.append('entity_type', entityType);
        formData.append('entity_id', entityId);
        formData.append('document_type', fileData.documentType);

        setUploadProgress(prev => ({ ...prev, [fileData.id]: 0 }));

        return api.post('/documents/upload', formData, {
          headers: {
            'Authorization': `Bearer ${authService.token}`
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [fileData.id]: progress }));
          }
        });
      });

      await Promise.all(uploadPromises);
      onSuccess?.();
      onHide();
      resetForm();
    } catch (error) {
      setError(error.response?.data?.error || 'Upload failed');
    } finally {
      setLoading(false);
      setUploadProgress({});
    }
  };

  const resetForm = () => {
    setFiles([]);
    setError('');
    setUploadProgress({});
  };

  if (!show) return null;

  return (
    <div className="document-upload-overlay">
      <div className="document-upload-modal">

        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e9ecef',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h4 style={{ margin: 0, color: '#333', fontWeight: '600' }}>Upload Documents</h4>
          {!required && (
            <button
              onClick={onHide}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                padding: '0',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#f8f9fa';
                e.target.style.color = '#333';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'none';
                e.target.style.color = '#666';
              }}
            >
              ×
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{
            padding: '24px',
            maxHeight: '60vh',
            overflowY: 'auto'
          }}>
            {error && (
              <div style={{
                background: '#f8d7da',
                color: '#721c24',
                padding: '12px',
                borderRadius: '6px',
                marginBottom: '20px',
                border: '1px solid #f5c6cb'
              }}>
                {error}
              </div>
            )}

            {files.length === 0 && (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#666',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '2px dashed #dee2e6'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
                <p style={{ margin: '0 0 16px 0', fontSize: '16px' }}>No documents added yet</p>
                <p style={{ margin: 0, fontSize: '14px' }}>Click "Add Document" to start uploading</p>
              </div>
            )}

            {files.map((fileData) => (
              <div key={fileData.id} style={{
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px',
                background: '#f8f9fa'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <h6 style={{ margin: 0, color: '#333' }}>Document {files.indexOf(fileData) + 1}</h6>
                  <button
                    type="button"
                    onClick={() => removeFile(fileData.id)}
                    style={{
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Remove
                  </button>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333' }}>Document Type *</label>
                  <select
                    value={fileData.documentType}
                    onChange={(e) => updateFile(fileData.id, 'documentType', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ced4da',
                      borderRadius: '6px',
                      fontSize: '14px',
                      background: 'white'
                    }}
                    required
                  >
                    <option value="">Select Document Type</option>
                    {documentTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333' }}>Document File *</label>
                  <input
                    type="file"
                    onChange={(e) => updateFile(fileData.id, 'file', e.target.files[0])}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ced4da',
                      borderRadius: '6px',
                      fontSize: '14px',
                      background: 'white'
                    }}
                    required
                  />
                  <small style={{ color: '#6c757d', fontSize: '12px' }}>
                    Supported: PDF, JPG, PNG, DOC, DOCX (Max 5MB each)
                  </small>
                </div>

                {uploadProgress[fileData.id] !== undefined && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontSize: '12px', color: '#666' }}>Uploading...</span>
                      <span style={{ fontSize: '12px', color: '#666' }}>{uploadProgress[fileData.id]}%</span>
                    </div>
                    <div style={{
                      background: '#e9ecef',
                      height: '4px',
                      borderRadius: '2px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        background: '#007bff',
                        height: '100%',
                        width: `${uploadProgress[fileData.id]}%`,
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={addFile}
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px dashed #007bff',
                background: 'transparent',
                color: '#007bff',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.background = '#007bff';
                  e.target.style.color = 'white';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#007bff';
                }
              }}
            >
              <span style={{ fontSize: '18px' }}>+</span>
              Add Document
            </button>
          </div>

          <div style={{
            padding: '16px 24px',
            borderTop: '1px solid #e9ecef',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            {!required && (
              <button
                type="button"
                onClick={onHide}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ced4da',
                  background: 'white',
                  color: '#6c757d',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.background = '#f8f9fa';
                    e.target.style.borderColor = '#adb5bd';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.target.style.background = 'white';
                    e.target.style.borderColor = '#ced4da';
                  }
                }}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={loading || files.filter(f => f.file && f.documentType).length === 0}
              style={{
                padding: '10px 20px',
                border: 'none',
                background: loading ? '#6c757d' : '#007bff',
                color: 'white',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                opacity: files.filter(f => f.file && f.documentType).length === 0 ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!loading && files.filter(f => f.file && f.documentType).length > 0) {
                  e.target.style.background = '#0056b3';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.background = '#007bff';
                }
              }}
            >
              {loading ? 'Uploading...' : `Upload ${files.filter(f => f.file && f.documentType).length} Document${files.filter(f => f.file && f.documentType).length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentUploadModal;